import { useCallback, useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'

import PerfectScrollbar from 'react-perfect-scrollbar'

import { Menu, MenuItem } from '@menu/vertical-menu'
import menuItemStyles from '@core/styles/vertical/menuItemStyles'
import menuSectionStyles from '@core/styles/vertical/menuSectionStyles'
import StyledVerticalNavExpandIcon, {
  StyledVerticalNavExpandIconWrapper
} from '@menu/styles/vertical/StyledVerticalNavExpandIcon'

import CustomButton from '@/components/CustomButton'
import showToastAlert from '@/components/Toast'
import { useUIStore } from '@/stores/uiStore'

import useVerticalNav from '@menu/hooks/useVerticalNav'
import primaryColorConfig from '@configs/primaryColorConfig'
import { useSettings } from '@core/hooks/useSettings'

type Props = {
  scrollMenu: (container: any, isPerfectScrollbar: boolean) => void
}

const RenderExpandIcon = ({ open, transitionDuration }: { open?: boolean; transitionDuration?: number }) => (
  <StyledVerticalNavExpandIconWrapper>
    <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
      <i className='ri-arrow-right-s-line' />
    </StyledVerticalNavExpandIcon>
  </StyledVerticalNavExpandIconWrapper>
)

const VerticalMenu = ({ scrollMenu }: Props) => {
  const theme = useTheme()
  const verticalNavOptions = useVerticalNav()
  const { isBreakpointReached, transitionDuration } = verticalNavOptions
  const selectedUsaha = useUIStore(state => state.selectedUsaha)
  const setSelectedUsahaStore = useUIStore(state => state.setSelectedUsaha)
  const hydrate = useUIStore(state => state.hydrateFromStorage)
  const { updateSettings } = useSettings()

  const [logout, setLogout] = useState(false)

  const handleUserLogout = useCallback(async () => {
    setLogout(true)

    try {
      // await signOut({ redirect: false } as const)
      sessionStorage.clear()
      window.location.href = '/login'
    } catch (error: unknown) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLogout(false)
    }
  }, [])

  const quickMenu = [
    { key: 'TMC', label: 'TMC', icon: '/images/icon/logo_color_tmc.png' },
    { key: 'SDP', label: 'SDP', icon: '/images/icon/logo_samco.png' }
  ] as const

  useEffect(() => {
    hydrate()
  }, [hydrate])

  useEffect(() => {
    const brandPrimary = selectedUsaha === 'SDP' ? '#b22315' : primaryColorConfig[0].main

    updateSettings({ primaryColor: brandPrimary })
  }, [selectedUsaha, updateSettings])

  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar

  return (
    <>
      <ScrollWrapper
        {...(isBreakpointReached
          ? {
              className: 'bs-full overflow-y-auto overflow-x-hidden',
              onScroll: (container: any) => scrollMenu(container, false)
            }
          : {
              options: { wheelPropagation: false, suppressScrollX: true },
              onScrollY: (container: any) => scrollMenu(container, true)
            })}
      >
        {/* Incase you also want to scroll NavHeader to scroll with Vertical Menu, remove NavHeader from above and paste it below this comment */}
        {/* Vertical Menu */}
        <Menu
          popoutMenuOffset={{ mainAxis: 17 }}
          menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
          renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
          renderExpandedMenuItemIcon={{ icon: <i className='ri-circle-fill' /> }}
          menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
        >
          {quickMenu.map(menu => {
            return (
              <MenuItem
                key={menu.key}
                href='/home'
                icon={
                  <div
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 90,
                      height: 36,
                      borderRadius: 8,
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
                    }}
                  >
                    <img src={menu.icon} alt={menu.label} style={{ width: 75, height: 25, objectFit: 'contain' }} />
                  </div>
                }
                onClick={() => setSelectedUsahaStore(menu.key)}
                rootStyles={{
                  marginBottom: '8px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                {menu.label}
              </MenuItem>
            )
          })}
        </Menu>
      </ScrollWrapper>

      <Box sx={{ p: 2, mt: 'auto' }}>
        <Divider sx={{ mb: 2, borderColor: '#fff' }} />
        <CustomButton
          fullWidth
          variant='contained'
          color='error'
          endIcon={<i className='ri-logout-box-r-line' />}
          onClick={handleUserLogout}
          loading={logout}
        >
          Logout
        </CustomButton>
      </Box>
    </>
  )
}

export default VerticalMenu
